import { useState } from 'react';
import { Link, useLoaderData, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Button } from '@immo/ui-kit';
import type {
  OrganizationRecordMovePlan,
  OrganizationRecordRef,
  OrganizationRecordResponsibilityChange,
  OrganizationRecordResponsibilityIds,
  OrganizationRecordResponsibleMember,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { readUuidParam, readUuidParams } from '@/lib/list-view/use-list-params';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { OrganizationsIcon } from '@/components/layout/icons';
import { RECORD_KINDS, type RecordSelection } from '@/pages/organizations/organization-records';

const OUTDATED_CODE = 'deal.record-move.outdated-preview';

type Organization = { readonly id: string; readonly name: string };

export type OrganizationMoveData = {
  readonly source: Organization;
  readonly target: Organization | null;
  readonly selection: RecordSelection;
  readonly plan: OrganizationRecordMovePlan | null;
  // The refusal the preview answered, when it answered one.
  readonly errorCode: string | null;
};

const toBody = (selection: RecordSelection) => ({
  deals: [...selection.deals],
  properties: [...selection.properties],
  contacts: [...selection.contacts],
  leads: [...selection.leads],
});

// What the operator confirms about responsibility, sent back by member id so the
// server refuses the move when it would now keep, remove or assign someone else.
const toResponsibilityIds = (change: OrganizationRecordResponsibilityChange): OrganizationRecordResponsibilityIds => ({
  kind: change.record.kind === 'LEAD' ? 'LEAD' : 'DEAL',
  id: change.record.id,
  kept: change.kept.map((member) => member.memberId),
  removed: change.removed.map((member) => member.memberId),
  assigned: change.assigned.map((member) => member.memberId),
});

function errorCodeOf(body: unknown): string | null {
  return (body as { error?: { code?: string } } | undefined)?.error?.code ?? null;
}

/**
 * The review of a Standort move (IMM-1104). The selection and the target live in
 * the address, so a reload re-plans the same move; the plan comes from the
 * dry-run preview and nothing is written until the operator confirms.
 */
export async function organizationMoveLoader({ params, request }: LoaderFunctionArgs): Promise<OrganizationMoveData> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) throw new Response('Missing organization id', { status: 404 });

  const search = new URL(request.url).searchParams;
  const targetId = readUuidParam(search, 'target');
  const selection: RecordSelection = {
    deals: readUuidParams(search, 'deals'),
    properties: readUuidParams(search, 'properties'),
    contacts: readUuidParams(search, 'contacts'),
    leads: readUuidParams(search, 'leads'),
  };
  const sourceResult = await apiClient.adminOrganizations.get({ pathParams: { id } });

  if (!sourceResult.ok) throw new Response('Failed to load organization', { status: sourceResult.status });

  const source = { id, name: sourceResult.body.name };
  const empty = RECORD_KINDS.every((kind) => selection[kind].length === 0);

  if (targetId === null || empty) return { source, target: null, selection, plan: null, errorCode: null };

  const [targetResult, previewResult] = await Promise.all([
    apiClient.adminOrganizations.get({ pathParams: { id: targetId } }),
    apiClient.adminDeals.previewOrganizationRecordMove({
      pathParams: { id },
      body: { targetOrganizationId: targetId, selection: toBody(selection) },
    }),
  ]);
  const target = targetResult.ok ? { id: targetId, name: targetResult.body.name } : { id: targetId, name: targetId };

  if (!previewResult.ok) {
    return { source, target, selection, plan: null, errorCode: errorCodeOf(previewResult.body) };
  }

  return { source, target, selection, plan: previewResult.body, errorCode: null };
}

const keyOf = (record: Pick<OrganizationRecordRef, 'kind' | 'id'>): string => `${record.kind}:${record.id}`;

export function OrganizationMovePage() {
  const t = useTranslations('pages.admin.organizations.move');
  const tList = useTranslations('pages.admin.organizations');
  const tErrors = useTranslations('errors');
  const { source, target, selection, plan, errorCode } = useLoaderData() as OrganizationMoveData;
  const revalidator = useRevalidator();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [moved, setMoved] = useState<OrganizationRecordMovePlan | null>(null);

  const shown = moved ?? plan;
  const kind = (record: OrganizationRecordRef): string => t(`kind.${record.kind}`);
  const linkedTo = (records: ReadonlyArray<OrganizationRecordRef>) =>
    records.map((record) => `${kind(record)} ${record.label}`).join(', ');

  const confirm = async (confirmed: OrganizationRecordMovePlan, to: Organization) => {
    setSubmitting(true);
    setFailure(null);

    try {
      const result = await apiClient.adminDeals.moveOrganizationRecords({
        pathParams: { id: source.id },
        body: {
          targetOrganizationId: to.id,
          selection: toBody(selection),
          expected: confirmed.moving,
          expectedResponsibility: confirmed.responsibility.map(toResponsibilityIds),
        },
      });

      if (result.ok) {
        setMoved(result.body);

        return;
      }

      const code = errorCodeOf(result.body);

      if (code === OUTDATED_CODE) {
        setFailure(t('outdated'));
        revalidator.revalidate();

        return;
      }

      setFailure(code === null ? t('failed') : tErrors(code));
    } catch {
      setFailure(t('failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const members = (list: ReadonlyArray<OrganizationRecordResponsibleMember>): string =>
    list.map((member) => member.displayName ?? t('responsibility.unknown-member')).join(', ');

  const responsibility = (record: OrganizationRecordRef) => {
    const change = shown?.responsibility.find((candidate) => keyOf(candidate.record) === keyOf(record));

    if (change === undefined) return null;

    return (
      <div className="text-sm text-text-secondary">
        {change.kept.length > 0 && <p>{t('responsibility.kept', { members: members(change.kept) })}</p>}
        {change.removed.length > 0 && <p>{t('responsibility.removed', { members: members(change.removed) })}</p>}
        {change.assignedVia !== null && change.assigned.length > 0 && (
          <p>{t(`responsibility.assigned.${change.assignedVia}`, { members: members(change.assigned) })}</p>
        )}
      </div>
    );
  };

  const responsibilityTotals = (changes: ReadonlyArray<OrganizationRecordResponsibilityChange>) => ({
    kept: changes.reduce((total, change) => total + change.kept.length, 0),
    removed: changes.reduce((total, change) => total + change.removed.length, 0),
    assigned: changes.reduce((total, change) => total + change.assigned.length, 0),
  });

  const shared = (record: { readonly staysLinkedTo: ReadonlyArray<OrganizationRecordRef> }) =>
    record.staysLinkedTo.length === 0 ? null : (
      <div className="text-sm text-text-tertiary">
        <p>{t('shared', { source: source.name })}</p>
        <p>{t('linked-to', { records: linkedTo(record.staysLinkedTo) })}</p>
      </div>
    );

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={OrganizationsIcon}
            crumbs={[
              { label: tList('title'), to: '/workspaces/organizations' },
              { label: source.name, to: `/workspaces/organizations/${source.id}` },
              { label: t('title') },
            ]}
          />
        }
      />
      <div className="space-y-6 px-6 py-6">
        {target !== null && (
          <h1 className="text-lg font-semibold text-text-primary">
            {t('heading', { source: source.name, target: target.name })}
          </h1>
        )}
        {target === null && <p className="text-sm text-text-secondary">{t('nothing-selected')}</p>}
        {target !== null && shown === null && (
          <p role="alert" className="text-sm text-danger-600">
            {errorCode === null ? t('plan-failed') : tErrors(errorCode)}
          </p>
        )}
        {shown !== null && (
          <p className="text-sm text-text-secondary">
            {t('totals', {
              deals: shown.moving.deals.length,
              properties: shown.moving.properties.length,
              contacts: shown.moving.contacts.length,
              leads: shown.moving.leads.length,
            })}
          </p>
        )}
        {moved !== null && target !== null && (
          <section className="space-y-2" aria-live="polite">
            <h2 className="text-base font-semibold text-text-primary">{t('success.title')}</h2>
            <p className="text-sm text-text-secondary">{t('success.body', { target: target.name })}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={`/workspaces/organizations/${source.id}`}
                className="text-interactive underline-offset-4 hover:underline"
              >
                {t('success.to-source', { source: source.name })}
              </Link>
              <Link
                to={`/workspaces/organizations/${target.id}`}
                className="text-interactive underline-offset-4 hover:underline"
              >
                {t('success.to-target', { target: target.name })}
              </Link>
            </div>
          </section>
        )}
        {shown !== null && moved === null && (
          <>
            {shown.responsibility.length > 0 && (
              <section className="space-y-1" aria-label={t('responsibility.heading')}>
                <h2 className="text-base font-semibold text-text-primary">{t('responsibility.heading')}</h2>
                <p className="text-sm text-text-secondary">
                  {t('responsibility.summary', responsibilityTotals(shown.responsibility))}
                </p>
                <p className="text-sm text-text-tertiary">{t('responsibility.note')}</p>
              </section>
            )}
            <ul className="space-y-4">
              {shown.selected.map((record) => {
                const brought = shown.included.filter((included) =>
                  included.via.some((via) => keyOf(via.record) === keyOf(record)),
                );

                return (
                  <li key={keyOf(record)} className="space-y-2 rounded-md border border-border p-4">
                    <div>
                      <p className="font-semibold text-text-primary">
                        {kind(record)} · {record.label}
                      </p>
                      {shared(record)}
                      {responsibility(record)}
                    </div>
                    <p className="text-sm font-semibold text-text-secondary">{t('brings-heading')}</p>
                    {brought.length === 0 ? (
                      <p className="text-sm text-text-tertiary">{t('brings-none')}</p>
                    ) : (
                      <ul className="space-y-2">
                        {brought.map((included) => {
                          const via = included.via.find((candidate) => keyOf(candidate.record) === keyOf(record));

                          return (
                            <li key={keyOf(included)}>
                              <p className="text-sm text-text-primary">
                                {kind(included)} · {included.label}
                              </p>
                              {via !== undefined && (
                                <p className="text-sm text-text-tertiary">
                                  {t(`relation.${via.relation}`, { label: record.label })}
                                </p>
                              )}
                              {shared(included)}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            {[...shown.selected, ...shown.included].some((record) => record.staysLinkedTo.length > 0) && (
              <p className="text-sm text-text-secondary">{t('shared-note')}</p>
            )}
            {failure !== null && (
              <p role="alert" className="text-sm text-danger-600">
                {failure}
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                disabled={submitting || revalidator.state === 'loading' || target === null}
                onClick={() => target !== null && confirm(shown, target)}
              >
                {submitting ? t('confirming') : t('confirm')}
              </Button>
              <Button variant="ghost" asChild>
                <Link to={`/workspaces/organizations/${source.id}`}>{t('back-cta')}</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
